// src/pages/Login.jsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  useLoginUserMutation,
  useEmployeeLoginMutation,
} from "../toolkitServices/userApi";
import { setUser } from "../slices/authSlice";
import { useDispatch } from "react-redux";
import AppLoader from "../components/AppLoader";
import {
  HiEye,
  HiEyeOff,
  HiPhone,
  HiLockClosed,
  HiMail,
} from "react-icons/hi";
import toast from "react-hot-toast";

const getApiErrorMessage = (err) => {
  if (!err) return "Login failed";

  const data = err?.data;

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return parsed?.msg || parsed?.message || data;
    } catch {
      return data;
    }
  }

  if (data && typeof data === "object") {
    return data?.msg || data?.message || "Login failed";
  }

  if (err?.message) return err.message;
  if (err?.error) return err.error;

  return "Login failed";
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loginType, setLoginType] = useState("owner"); // owner | employee
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      mobile: "",
      officialEmail: "",
      password: "",
    },
  });

  const [loginUser, { isLoading: isOwnerLoading }] = useLoginUserMutation();
  const [employeeLogin, { isLoading: isEmployeeLoading }] =
    useEmployeeLoginMutation();

  const isLoading = isOwnerLoading || isEmployeeLoading;

  const handleTabChange = (type) => {
    setLoginType(type);
    setAuthError("");
    setShowPassword(false);
    clearErrors();
    reset({
      mobile: "",
      officialEmail: "",
      password: "",
    });
  };

  const onSubmit = async (formData) => {
    setAuthError("");

    try {
      let res;

      if (loginType === "owner") {
        res = await loginUser({
          mobile: formData.mobile,
          password: formData.password,
        }).unwrap();
      } else {
        res = await employeeLogin({
          officialEmail: formData.officialEmail,
          password: formData.password,
        }).unwrap();
      }

      if (res?.msg && !res?.data) {
        setAuthError(res.msg);
        toast.error(res.msg);
        return;
      }

      dispatch(setUser(res?.data));

      toast.success(
        loginType === "owner"
          ? "Login successful!"
          : "Employee login successful!"
      );

      if (res?.data?.role === "staff") {
        navigate("/profile");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setAuthError(msg);
      toast.error(msg);
      console.error("Login failed:", err);
    }
  };

  if (isLoading) return <AppLoader />;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-[#f5f3ff] via-[#fefce8] to-[#e0f2fe] px-3">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(196,181,253,0.28),transparent_55%),radial-gradient(circle_at_bottom,_rgba(251,191,36,0.18),transparent_55%)]" />
        <div className="absolute -top-24 left-6 h-56 w-56 rounded-full bg-[#c4b5fd]/40 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex w-full max-w-4xl overflow-hidden rounded-3xl bg-white/90 shadow-xl shadow-violet-200/70 ring-1 ring-violet-100 backdrop-blur-md md:h-[500px]"
      >
        <div className="flex w-full flex-col md:flex-row">
          <div className="flex w-full flex-col justify-center border-slate-100 px-5 py-5 sm:px-7 sm:py-6 md:w-1/2 md:border-r md:px-8 md:py-7">
            <div className="mb-4">
              <Link
                to="/"
                className="cursor-pointer text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600"
              >
                SwitchIn
              </Link>

              <h2 className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl md:text-2xl">
                Sign in to your workspace
              </h2>

              <p className="mt-2 text-[11px] text-slate-500 sm:text-xs md:text-sm">
                {loginType === "owner"
                  ? "Login as owner/admin to manage your jewellery business."
                  : "Login as employee with your official company email."}
              </p>
            </div>

            {/* Login Type Tabs */}
            <div className="mb-4 grid grid-cols-2 rounded-2xl bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => handleTabChange("owner")}
                className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                  loginType === "owner"
                    ? "bg-white text-violet-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Owner / Admin
              </button>

              <button
                type="button"
                onClick={() => handleTabChange("employee")}
                className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                  loginType === "employee"
                    ? "bg-white text-violet-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Employee
              </button>
            </div>

            {authError ? (
              <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {authError}
              </div>
            ) : null}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5">
              {loginType === "owner" ? (
                <div>
                  <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                    Mobile Number
                  </label>

                  <div
                    className={`group mt-1 flex h-[44px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.mobile ? "border-red-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[54px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiPhone className="text-sm" />
                      </span>
                    </div>

                    <input
                      {...register("mobile", {
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: "Enter a valid 10 digit mobile number",
                        },
                      })}
                      className="h-full w-full bg-transparent px-3 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="9876543210"
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                      }}
                    />
                  </div>

                  {errors.mobile && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.mobile.message}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="text-[11px] font-medium text-slate-700 md:text-xs">
                    Official Email
                  </label>

                  <div
                    className={`group mt-1 flex h-[44px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                      errors.officialEmail
                        ? "border-red-400"
                        : "border-slate-200"
                    }`}
                  >
                    <div className="flex h-full min-w-[54px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                        <HiMail className="text-sm" />
                      </span>
                    </div>

                    <input
                      {...register("officialEmail", {
                        required: "Official email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
                          message: "Enter a valid official email",
                        },
                      })}
                      className="h-full w-full bg-transparent px-3 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                      type="email"
                      placeholder="employee@company.com"
                      autoComplete="email"
                    />
                  </div>

                  {errors.officialEmail && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.officialEmail.message}
                    </p>
                  )}
                </div>
              )}

              {/* Password */}
              <div>
                <label className="flex items-center justify-between text-[11px] font-medium text-slate-700 md:text-xs">
                  <span>Password</span>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        loginType === "owner"
                          ? "/forgot-password"
                          : "/employee-forgot-password"
                      )
                    }
                    className="text-[11px] font-medium text-violet-600 hover:underline"
                  >
                    Forgot password?
                  </button>
                </label>

                <div
                  className={`group relative mt-1 flex h-[44px] items-center overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 focus-within:border-violet-500 focus-within:shadow-md focus-within:shadow-violet-100 focus-within:ring-2 focus-within:ring-violet-100 ${
                    errors.password ? "border-red-400" : "border-slate-200"
                  }`}
                >
                  <div className="flex h-full min-w-[54px] shrink-0 items-center justify-center border-r border-slate-200 bg-gradient-to-r from-violet-50 to-slate-50">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-violet-600 shadow-sm ring-1 ring-violet-100">
                      <HiLockClosed className="text-sm" />
                    </span>
                  </div>

                  <input
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Minimum 6 characters required",
                      },
                    })}
                    className="h-full w-full bg-transparent px-3 pr-11 text-sm font-medium text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-violet-50 hover:text-violet-600"
                  >
                    {showPassword ? <HiEyeOff /> : <HiEye />}
                  </button>
                </div>

                {errors.password && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <button
                disabled={isLoading}
                type="submit"
                className="mt-1 inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-400/60 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-80"
              >
                {isLoading
                  ? "Signing in..."
                  : loginType === "owner"
                    ? "Continue as Owner/Admin"
                    : "Continue as Employee"}
              </button>
            </form>

            {loginType === "owner" ? (
              <p className="mt-4 text-center text-[11px] text-slate-500">
                Don&apos;t have an account?{" "}
                <Link
                  to="/register"
                  className="font-medium text-violet-600 hover:underline"
                >
                  Register your store
                </Link>
              </p>
            ) : (
              <p className="mt-4 text-center text-[11px] text-slate-500">
                Employee account is created by your store owner/admin.
              </p>
            )}
          </div>

          <div className="relative hidden bg-gradient-to-br from-[#312e81] via-[#4c1d95] to-[#f97316] text-white md:flex md:w-1/2">
            <div className="flex flex-1 items-center justify-center px-6 py-7">
              <div className="relative w-full max-w-sm">
                <div className="absolute -inset-6 rounded-[32px] bg-white/15 blur-3xl" />

                <div className="relative flex items-center justify-center overflow-hidden rounded-[28px] bg-slate-900/30 shadow-2xl shadow-black/40 ring-1 ring-white/25 backdrop-blur-sm">
                  <img
                    src="/welcome.svg"
                    alt="Welcome to SwitchIn"
                    className="h-64 w-full object-contain p-5"
                  />
                </div>
              </div>
            </div>

            <p className="absolute bottom-5 right-6 text-[10px] text-white/75">
              Jewellery billing · Stock · Schemes
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;